import { useContext, useEffect, useState } from "react";
import { AccountContext, WebSocketContext } from "../App.tsx";
import { EventBus } from "../game/EventBus";
import { EVENTS_NAME } from "../game/consts.ts";
import { completeObjective } from "../api.ts";

const FINAL_MISSION_ID = 9000;
const HUMAN_OBJECTIVE_ID = 1;
const AI_OBJECTIVE_ID = 2;

// Persistent, always-mounted (not the ChallengeDialog popover — this has no
// zone or NPC of its own; it's unlocked globally the instant the boss dies,
// server-side, for every connected player at once). Renders nothing until
// unlocked; once unlocked it never goes away again.
//
// Per-objective feedback is inherently a little approximate: the server only
// sends an explicit message on a WRONG answer (ErrResponse/wrongPhrase) or on
// full mission completion (MissionComplete/correctPhrase) — a lone correct
// objective just updates the account's missions_in_progress bitmask silently
// (see ro-game/src/lib.rs ObjectiveCompletionPhrase handler). So: bit state
// comes from `account` reactively (reliable), while the transient "was that
// right or wrong" messaging is gated on a local pendingObjective flag, same
// idiom ChallengeDialog itself uses — a real collision (another challenge
// resolving in the exact same instant) is low-risk enough to accept for v1.
function FinalAnswersWidget() {
  const [account] = useContext(AccountContext);
  const websocket = useContext(WebSocketContext);
  const [unlocked, setUnlocked] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [humanAnswer, setHumanAnswer] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");
  const [pendingObjective, setPendingObjective] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<{ objectiveId: number; ok: boolean } | null>(null);
  const [endStory, setEndStory] = useState<string | null>(null);

  useEffect(() => {
    const onWorldState = (s: { boss_defeated: boolean }) => {
      if (s.boss_defeated) setUnlocked(true);
    };
    EventBus.on(EVENTS_NAME.worldState, onWorldState);
    return () => EventBus.removeListener(EVENTS_NAME.worldState, onWorldState);
  }, []);

  // Fallback for the case the WorldState message was somehow missed: if this
  // player already has the mission (e.g. it was granted on a previous join),
  // that alone proves the boss is dead.
  const missionProgress = (account as any)?.missions_in_progress?.find(
    (m: any) => parseInt(m.mission_id) === FINAL_MISSION_ID,
  );
  useEffect(() => {
    if (missionProgress) setUnlocked(true);
  }, [missionProgress]);

  useEffect(() => {
    const onWrong = () => {
      if (pendingObjective !== null) {
        setFeedback({ objectiveId: pendingObjective, ok: false });
        setPendingObjective(null);
      }
    };
    const onCorrect = (message: string) => {
      if (pendingObjective !== null) {
        setFeedback({ objectiveId: pendingObjective, ok: true });
        // Only a FULL mission completion carries real story text (a lone
        // correct objective sends no message at all) -- a generic fallback
        // line would otherwise show up here on the first of the two.
        if (missionProgress?.mission_end_time) {
          setEndStory(message);
        }
        setPendingObjective(null);
      }
    };
    EventBus.on(EVENTS_NAME.wrongPhrase, onWrong);
    EventBus.on(EVENTS_NAME.correctPhrase, onCorrect);
    return () => {
      EventBus.removeListener(EVENTS_NAME.wrongPhrase, onWrong);
      EventBus.removeListener(EVENTS_NAME.correctPhrase, onCorrect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingObjective]);

  if (!unlocked) return null;

  const objectivesComplete = missionProgress ? Number(missionProgress.objectives_complete) : 0;
  const humanDone = (objectivesComplete & (1 << HUMAN_OBJECTIVE_ID)) !== 0;
  const aiDone = (objectivesComplete & (1 << AI_OBJECTIVE_ID)) !== 0;
  const bothDone = !!missionProgress?.mission_end_time;

  const submit = (objectiveId: number, answer: string) => {
    if (!websocket || !answer.trim()) return;
    setPendingObjective(objectiveId);
    setFeedback(null);
    completeObjective(websocket, FINAL_MISSION_ID, objectiveId, answer.trim());
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: 10,
        left: 10,
        zIndex: 1000,
        fontFamily: "inherit",
      }}
    >
      {!expanded ? (
        <button
          onClick={() => setExpanded(true)}
          style={{
            background: "black",
            border: "1px solid var(--ro-accent)",
            color: "var(--ro-accent-bright)",
            borderRadius: 6,
            padding: "8px 14px",
            letterSpacing: "0.08em",
            fontWeight: "bold",
            cursor: "pointer",
            boxShadow: bothDone ? "none" : "0 0 10px rgba(31,168,224,0.6)",
          }}
        >
          {bothDone ? "✓ FINAL ANSWERS" : "FINAL ANSWERS"}
        </button>
      ) : (
        <div
          style={{
            background: "#111417",
            border: "1px solid var(--ro-accent)",
            borderRadius: 6,
            padding: 14,
            width: 320,
            color: "var(--ro-text)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <div style={{ fontWeight: "bold", letterSpacing: "0.1em" }}>
              FINAL ANSWERS
            </div>
            <button
              onClick={() => setExpanded(false)}
              style={{
                background: "none",
                border: "none",
                color: "var(--ro-text)",
                cursor: "pointer",
                fontSize: "1.1em",
              }}
              aria-label="Collapse"
            >
              ×
            </button>
          </div>

          <AnswerRow
            label="HUMAN"
            done={humanDone}
            answer={humanAnswer}
            setAnswer={setHumanAnswer}
            onSubmit={() => submit(HUMAN_OBJECTIVE_ID, humanAnswer)}
            feedback={feedback?.objectiveId === HUMAN_OBJECTIVE_ID ? feedback.ok : null}
            pending={pendingObjective === HUMAN_OBJECTIVE_ID}
          />
          <AnswerRow
            label="AI"
            done={aiDone}
            answer={aiAnswer}
            setAnswer={setAiAnswer}
            onSubmit={() => submit(AI_OBJECTIVE_ID, aiAnswer)}
            feedback={feedback?.objectiveId === AI_OBJECTIVE_ID ? feedback.ok : null}
            pending={pendingObjective === AI_OBJECTIVE_ID}
          />

          {bothDone && (
            <div
              style={{
                marginTop: 12,
                paddingTop: 10,
                borderTop: "1px solid var(--ro-accent)",
                fontStyle: "italic",
                whiteSpace: "pre-wrap",
                color: "var(--ro-accent-bright)",
              }}
            >
              {endStory || "Both names confirmed."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AnswerRow({
  label,
  done,
  answer,
  setAnswer,
  onSubmit,
  feedback,
  pending,
}: {
  label: string;
  done: boolean;
  answer: string;
  setAnswer: (v: string) => void;
  onSubmit: () => void;
  feedback: boolean | null;
  pending: boolean;
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: ".85em", letterSpacing: "0.06em", marginBottom: 4 }}>
        {label}:
      </div>
      {done ? (
        <div style={{ color: "var(--ro-accent-bright)" }}>✓ confirmed</div>
      ) : (
        <div style={{ display: "flex", gap: 6 }}>
          <input
            className="Input"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSubmit();
            }}
            style={{ flex: 1 }}
          />
          <button className="Button green" onClick={onSubmit} disabled={pending}>
            {pending ? "..." : "OK"}
          </button>
        </div>
      )}
      {feedback === false && (
        <div style={{ color: "#e05050", fontSize: ".8em", marginTop: 2 }}>
          Not it. Try again.
        </div>
      )}
    </div>
  );
}

export default FinalAnswersWidget;
